import { Injectable } from '@angular/core';
import { State } from './+state/drupal-data.reducer';
import { select, Store } from '@ngrx/store';
import { ComponentStore } from '@ngrx/component-store';
import { combineLatest, Observable, of, take, tap } from 'rxjs';
import { JsonApiQuery } from './json-api.query';
import { selectRequestById } from './+state/drupal-data.selectors';
import { sendRequest, setMenu } from './+state/drupal-data.actions';
import { Tree } from '@nx-icilalune/drupal-types';
import { DataAccessModule } from './data-access.module';

export interface Facade {
  load(query: JsonApiQuery): void;

  loadCollection(queries: JsonApiQuery[]): void;

  setMenu(menu: Tree<any>, key: string): void;

  getQuery$(id: string): Observable<JsonApiQuery | undefined>;
}

@Injectable({
  providedIn: DataAccessModule,
})
export class FacadeService extends ComponentStore<any> implements Facade {
  #sendRequest = this.effect(
    (
      combined$: Observable<
        [JsonApiQuery | undefined, JsonApiQuery | undefined]
      >
    ) => {
      return combined$.pipe(
        tap(([request, existingRequest]) => {
          if (
            request &&
            ((!existingRequest?.loading && !existingRequest?.complete) ||
              ((request?.page?.offset === 0 || !request?.page?.offset) &&
                !request.id &&
                request.editable))
          ) {
            this.store.dispatch(sendRequest({ request }));
          }
        })
      );
    }
  );

  constructor(private store: Store<State>) {
    super();
  }

  load(query: JsonApiQuery): void {
    this.#manageQuery(query);
  }

  loadCollection(queries: JsonApiQuery[]): void {
    queries.forEach((query) => {
      this.#manageQuery(query);
    });
  }

  setMenu(menu: Tree<any>, key: string): void {
    this.store.dispatch(setMenu({ menu, key }));
  }

  getQuery$(id: string): Observable<JsonApiQuery | undefined> {
    return this.store.pipe(select(selectRequestById(id)), take(1));
  }

  #manageQuery(query: JsonApiQuery) {
    let queryId: string = query.queryId;
    if (query.id) {
      queryId = `${query.queryId}-${query.id}`;
    }
    const existingRequest$ = this.getQuery$(queryId);
    this.#sendRequest(combineLatest([of(query), existingRequest$]));
  }
}
